import { DateTime } from "luxon";
import classNames from "classnames";
import { useFieldArray } from "react-hook-form";
import { Table, Select, MinusIcon, IconButton, SelectOption } from "@mwi/ui";
import { Fragment } from "react";

type SubPeriodProps = {
  control: any;
  weekDayIdx: number;
  timeOptions: string[];
  watch: any;
  errors: any;
  setValue: any;
  disabled: boolean;
};

const SubPeriods = ({
  watch,
  errors,
  control,
  setValue,
  disabled,
  weekDayIdx,
  timeOptions,
}: SubPeriodProps) => {
  const { fields: periods, remove } = useFieldArray({
    control,
    name: `week_days.${weekDayIdx}.periods`,
  });

  // eslint-disable-next-line max-len
  const handleTimeIntervals = (
    periodIdx: number,
  ): { otOptions: string[]; ctOptions: string[] } => {
    const openingTime = DateTime.fromISO(
      watch(`week_days.${weekDayIdx}.periods.${periodIdx}.opening_time`),
    );
    const closingTime = DateTime.fromISO(
      watch(`week_days.${weekDayIdx}.periods.${periodIdx}.closing_time`),
    );

    const prevPeriodClosingTime = DateTime.fromISO(
      watch(`week_days.${weekDayIdx}.periods.${periodIdx - 1}.closing_time`),
    );

    // only allow open time intervals after the previous closing time interval
    let otOptions = timeOptions;
    if (prevPeriodClosingTime?.isValid) {
      otOptions = timeOptions.filter(
        (ot) => ot > prevPeriodClosingTime.toFormat("T"),
      );
    }

    // only allow closing time intervals after the open time interval
    let ctOptions = otOptions;
    if (openingTime?.isValid) {
      ctOptions = timeOptions.filter((ct) => ct > openingTime.toFormat("T"));
    }

    // clear selected intervals if previous closing time interval exceeds opening time interval
    if (
      prevPeriodClosingTime.isValid &&
      openingTime.isValid &&
      prevPeriodClosingTime >= openingTime
    ) {
      setTimeout(() => {
        setValue(
          `week_days.${weekDayIdx}.periods.${periodIdx}.opening_time`,
          null,
        );
        setValue(
          `week_days.${weekDayIdx}.periods.${periodIdx}.closing_time`,
          null,
        );
        if (watch(`week_days.${weekDayIdx}.periods`).length - 1 > periodIdx) {
          setValue(`week_days.${weekDayIdx}.periods`, [
            ...watch(`week_days.${weekDayIdx}.periods`).slice(0, periodIdx + 1),
          ]);
        }
      }, 0);
    }

    // clear selected intervals if opening time interval exceeds closing time interval
    if (
      openingTime?.isValid &&
      closingTime?.isValid &&
      openingTime >= closingTime
    ) {
      setTimeout(() => {
        setValue(
          `week_days.${weekDayIdx}.periods.${periodIdx}.closing_time`,
          null,
        );
        if (watch(`week_days.${weekDayIdx}.periods`).length - 1 > periodIdx) {
          setValue(`week_days.${weekDayIdx}.periods`, [
            ...watch(`week_days.${weekDayIdx}.periods`).slice(0, periodIdx + 1),
          ]);
        }
      }, 0);
    }

    return { otOptions, ctOptions };
  };

  return (
    <>
      {periods &&
        periods?.length &&
        periods.map((period, idx) => {
          if (idx === 0) {
            return <Fragment key={period.id} />;
          }

          const { otOptions, ctOptions } = handleTimeIntervals(idx);

          return (
            <Table.Row
              key={period.id}
              className={classNames("", {
                "bg-grey-lightest": disabled,
                "border-b border-grey-light transition":
                  idx + 1 === periods.length,
              })}
            >
              <Table.Item className="!py-2"></Table.Item>
              <Table.Item className="!py-2"></Table.Item>
              <Table.Item className="relative !py-2 pr-8">
                <Select
                  size="small"
                  control={control}
                  disabled={disabled}
                  placeholder="Select"
                  name={`week_days.${weekDayIdx}.periods.${idx}.opening_time`}
                  buttonClassName={classNames(
                    "!px-4 !rounded",
                    "text-base",
                    "shadow-none !bg-grey-lightest !border-grey-lightest",
                    "!focus:border !focus:border-primary",
                    {
                      "!border-error-light":
                        errors?.week_days?.[weekDayIdx]?.periods?.[idx]
                          ?.closing_time,
                    },
                  )}
                >
                  {otOptions &&
                    otOptions.map((s, idx) => (
                      <SelectOption key={idx} value={s}>
                        {s}
                      </SelectOption>
                    ))}
                </Select>

                <div className="absolute inset-y-1/2 right-[-3px] w-[7px] h-[2px] bg-black"></div>
              </Table.Item>
              <Table.Item className="!py-2 pl-8">
                <Select
                  size="small"
                  control={control}
                  disabled={
                    disabled ||
                    !watch(
                      `week_days.${weekDayIdx}.periods.${idx}.opening_time`,
                    )
                  }
                  placeholder="Select"
                  name={`week_days.${weekDayIdx}.periods.${idx}.closing_time`}
                  buttonClassName={classNames(
                    "!px-4 !rounded",
                    "text-base !bg-grey-lightest",
                    "shadow-none !border-grey-lightest",
                    "!focus:border !focus:border-primary",
                    {
                      "!border-error-light":
                        errors?.week_days?.[weekDayIdx]?.periods?.[idx]
                          ?.closing_time,
                    },
                  )}
                >
                  {ctOptions &&
                    ctOptions.map((s, idx) => (
                      <SelectOption key={idx} value={s}>
                        {s}
                      </SelectOption>
                    ))}
                </Select>
              </Table.Item>
              <Table.Item className="py-2">
                <IconButton disabled={disabled} onClick={() => remove(idx)}>
                  <MinusIcon className="w-4 h-4" />
                </IconButton>
              </Table.Item>
            </Table.Row>
          );
        })}
    </>
  );
};

export default SubPeriods;
